/**
 * Hook for searching and highlighting evidence quotes in PDF text layer
 *
 * Uses pdfjs text content API to find text positions and generate
 * SVG highlight rectangles for evidence quotes.
 */

import { useState, useEffect, useCallback } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'

// Domain colors for entity types
const DOMAIN_COLORS = {
  market: '#5c8a6b',
  business: '#3d7a8c',
  solution: '#c9a857',
  insights: '#635849',
}

// Entity type to domain mapping
const MARKET_TYPES = [
  'Company', 'Industry', 'Competitor', 'Persona', 'Person',
  'Problem', 'CustomerSegment', 'Market',
]
const BUSINESS_TYPES = [
  'ValueProposition', 'Channel', 'RevenueStream', 'Strategy',
  'KeyActivity', 'KeyResource', 'StrategicGoal', 'Partnership',
]
const SOLUTION_TYPES = [
  'Product', 'Feature', 'Requirement', 'Constraint', 'Technology',
]

export interface TextHighlight {
  pageNumber: number
  rects: Array<{
    x: number
    y: number
    width: number
    height: number
  }>
  entityId: string
  entityType: string
  entityName: string
  color: string
  quote: string
}

export interface EvidenceQuote {
  quote: string
  entityId: string
  entityType: string
  entityName: string
}

interface TextItem {
  str: string
  transform: number[]
  width: number
  height: number
  startIndex: number
  endIndex: number
}

function getEntityColor(entityType: string): string {
  if (MARKET_TYPES.includes(entityType)) return DOMAIN_COLORS.market
  if (BUSINESS_TYPES.includes(entityType)) return DOMAIN_COLORS.business
  if (SOLUTION_TYPES.includes(entityType)) return DOMAIN_COLORS.solution
  return DOMAIN_COLORS.insights
}

/**
 * Extract text items with positions from a PDF page
 */
async function getPageTextItems(
  pdfDoc: PDFDocumentProxy,
  pageNumber: number
): Promise<{ items: TextItem[]; fullText: string }> {
  const page = await pdfDoc.getPage(pageNumber)
  const textContent = await page.getTextContent()

  const items: TextItem[] = []
  let fullText = ''

  for (const item of textContent.items) {
    if (!('str' in item) || !item.str) continue

    const startIndex = fullText.length
    fullText += item.str
    const endIndex = fullText.length

    items.push({
      str: item.str,
      transform: item.transform as number[],
      width: item.width || 0,
      height: item.height || 10,
      startIndex,
      endIndex,
    })
  }

  return { items, fullText }
}

/**
 * Find all occurrences of a quote in text and map to text items
 */
function findQuoteInTextItems(
  quote: string,
  items: TextItem[],
  fullText: string,
  pageHeight: number,
  scale: number
): Array<{ x: number; y: number; width: number; height: number }> {
  const rects: Array<{ x: number; y: number; width: number; height: number }> = []
  const quoteLower = quote.toLowerCase().trim()
  const textLower = fullText.toLowerCase()

  // Skip very short quotes
  if (quoteLower.length < 3) return rects

  let searchStart = 0
  while (searchStart < textLower.length) {
    const matchIndex = textLower.indexOf(quoteLower, searchStart)
    if (matchIndex === -1) break

    const matchEnd = matchIndex + quoteLower.length

    // Find all text items that overlap with this match
    for (const item of items) {
      // Check if this item overlaps with the match
      if (item.endIndex <= matchIndex || item.startIndex >= matchEnd) continue

      // This item contains part of the match
      const [scaleX, , , scaleY, x, y] = item.transform
      const itemWidth = item.width * Math.abs(scaleX)
      const itemHeight = item.height * Math.abs(scaleY) || 12

      // PDF coordinates are bottom-left origin, convert to top-left
      const rectY = pageHeight - y - itemHeight

      rects.push({
        x: x * scale,
        y: rectY * scale,
        width: itemWidth * scale,
        height: itemHeight * scale,
      })
    }

    searchStart = matchIndex + 1
  }

  return rects
}

/**
 * Merge overlapping rectangles to avoid visual clutter
 */
function mergeRects(
  rects: Array<{ x: number; y: number; width: number; height: number }>
): Array<{ x: number; y: number; width: number; height: number }> {
  if (rects.length <= 1) return rects

  // Sort by y then x
  const sorted = [...rects].sort((a, b) => {
    const yDiff = a.y - b.y
    if (Math.abs(yDiff) > 5) return yDiff
    return a.x - b.x
  })

  const merged: typeof rects = []
  let current = sorted[0]

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i]

    // Check if on same line and overlapping/adjacent
    const sameLineY = Math.abs(current.y - next.y) < 5
    const overlapsX = next.x <= current.x + current.width + 5

    if (sameLineY && overlapsX) {
      // Merge rectangles
      const newX = Math.min(current.x, next.x)
      const newRight = Math.max(current.x + current.width, next.x + next.width)
      current = {
        x: newX,
        y: Math.min(current.y, next.y),
        width: newRight - newX,
        height: Math.max(current.height, next.height),
      }
    } else {
      merged.push(current)
      current = next
    }
  }
  merged.push(current)

  return merged
}

export interface UsePdfTextHighlightsResult {
  highlights: Map<number, TextHighlight[]>
  isSearching: boolean
  error: string | null
  pageCount: number
}

/**
 * Hook to search for evidence quotes in PDF and return highlight positions
 */
export function usePdfTextHighlights(
  pdfDoc: PDFDocumentProxy | null,
  evidenceQuotes: EvidenceQuote[],
  scale: number,
  pageHeights: Map<number, number>
): UsePdfTextHighlightsResult {
  const [highlights, setHighlights] = useState<Map<number, TextHighlight[]>>(new Map())
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pageCount, setPageCount] = useState(0)

  const searchPages = useCallback(async () => {
    if (!pdfDoc || evidenceQuotes.length === 0) {
      setHighlights(new Map())
      return
    }

    setIsSearching(true)
    setError(null)

    try {
      const numPages = pdfDoc.numPages
      setPageCount(numPages)
      const newHighlights = new Map<number, TextHighlight[]>()

      // Process each page
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const pageHeight = pageHeights.get(pageNum) || 842 // A4 default
        const { items, fullText } = await getPageTextItems(pdfDoc, pageNum)

        if (items.length === 0) continue

        const pageHighlights: TextHighlight[] = []

        // Search for each evidence quote
        for (const evidence of evidenceQuotes) {
          const rects = findQuoteInTextItems(
            evidence.quote,
            items,
            fullText,
            pageHeight,
            scale
          )

          if (rects.length > 0) {
            pageHighlights.push({
              pageNumber: pageNum,
              rects: mergeRects(rects),
              entityId: evidence.entityId,
              entityType: evidence.entityType,
              entityName: evidence.entityName,
              color: getEntityColor(evidence.entityType),
              quote: evidence.quote,
            })
          }
        }

        if (pageHighlights.length > 0) {
          newHighlights.set(pageNum, pageHighlights)
        }
      }

      setHighlights(newHighlights)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search PDF')
    } finally {
      setIsSearching(false)
    }
  }, [pdfDoc, evidenceQuotes, scale, pageHeights])

  useEffect(() => {
    searchPages()
  }, [searchPages])

  return { highlights, isSearching, error, pageCount }
}

export default usePdfTextHighlights
